/**
 * Builder HTML pour le Bottin des étudiants finalistes (document ministériel).
 *
 * Modèle officiel — deux pages, A4 paysage.
 * Page 1 :
 *   - En-tête : logo + titre
 *   - Année académique + Établissement + Cycle
 *   - 1. Informations générales (responsable + effectif)
 *   - 2. Liste des étudiants finalistes (grand tableau)
 *   - 3. Récapitulatif par filière (petit tableau)
 * Page 2 :
 *   - 4. Observations (lignes vides pour remplissage manuel au stylo)
 *   - Attestation du DAAC
 *   - "Fait à …, le …"
 *
 * Les moyennes S1-S6 manquantes signifient que l'étudiant n'était pas encore
 * à 2i cette année-là (arrivé en L2 ou L3 en cours de cursus).
 */

import type { DonneesBottin } from '../../services/pvDeliberationApi';

export interface BottinOptions {
  /** Ex: "Pointe-Noire" ou "Brazzaville". Défaut : celui du back. */
  ville?: string;
  /** Nationalité par défaut appliquée aux étudiants sans champ dédié. */
  nationaliteParDefaut?: string;
  /** Date à imprimer sur la ligne "Fait à …, le …" (ISO ou français). */
  dateEmission?: string;
}

const fmt = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '';
  return v.toFixed(2).replace('.', ',');
};

const fmtSexe = (s: string): string => {
  const v = (s || '').trim().toUpperCase();
  if (v === 'M' || v === 'MASCULIN' || v === 'H') return 'M';
  if (v === 'F' || v === 'FEMININ' || v === 'FÉMININ' || v === 'FEMME') return 'F';
  return v ? v.charAt(0) : '';
};

const fmtNaissance = (s: string | null | undefined): string => {
  if (!s) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  return s;
};

const fmtDate = (iso: string | undefined): string => {
  if (!iso) return '..................';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// Builder principal
// ═══════════════════════════════════════════════════════════════════════════

export function buildBottinHTML(data: DonneesBottin, options: BottinOptions = {}): string {
  const {
    ville = data.campus || 'Pointe-Noire',
    nationaliteParDefaut = 'Congolaise',
    dateEmission,
  } = options;

  const anneeAff = data.annee_academique.replace('-', ' – ');
  const etablissement = `INSTITUT INTERNATIONAL 2i, campus de ${data.campus || 'Pointe-Noire'}`;
  const cycleAff = data.cycle || 'LICENCE (L3)';
  const responsable = data.responsable_academique || '';

  // ── Lignes du grand tableau ──
  let rowsHtml = '';
  data.etudiants.forEach((e, i) => {
    const nat = (e.nationalite || nationaliteParDefaut).trim();
    // Année d'entrée : "2022-2023" → "2022"
    const anneeEntreeAff = (e.annee_entree || '').split('-')[0] || '';
    rowsHtml += `<tr>
      <td class="c">${i + 1}</td>
      <td class="l"><strong>${escapeHtml(e.nom)}</strong> ${escapeHtml(e.prenoms)}</td>
      <td class="c">${fmtSexe(e.sexe)}</td>
      <td class="c">${fmtNaissance(e.date_naissance)}</td>
      <td class="c">${escapeHtml(nat)}</td>
      <td class="c">${escapeHtml(e.matricule || '')}</td>
      <td class="l">${escapeHtml(e.parcours || '')}</td>
      <td class="c">${escapeHtml(anneeEntreeAff)}</td>
      <td class="c moy">${fmt(e.moyenne_s1)}</td>
      <td class="c moy">${fmt(e.moyenne_s2)}</td>
      <td class="c moy">${fmt(e.moyenne_s3)}</td>
      <td class="c moy">${fmt(e.moyenne_s4)}</td>
      <td class="c moy">${fmt(e.moyenne_s5)}</td>
      <td class="c moy">${fmt(e.moyenne_s6)}</td>
      <td class="c">${escapeHtml(e.telephone || '')}</td>
      <td class="l">${escapeHtml(e.email || '')}</td>
    </tr>`;
  });

  // ── Lignes du récapitulatif par filière ──
  let recapRows = '';
  let totalGeneral = 0;
  let totalFillesGeneral = 0;
  data.recapitulatif_par_filiere.forEach(r => {
    totalGeneral += r.effectif_total;
    totalFillesGeneral += r.dont_filles;
    recapRows += `<tr>
      <td class="l"><strong>${escapeHtml(r.parcours)}</strong></td>
      <td class="c">${escapeHtml(r.niveau)}</td>
      <td class="c">${r.effectif_total}</td>
      <td class="c">${r.dont_filles}</td>
    </tr>`;
  });
  // Ligne "Ensemble"
  recapRows += `<tr class="total">
    <td class="l"><strong>Ensemble</strong></td>
    <td class="c">${escapeHtml(data.recapitulatif_par_filiere[0]?.niveau || 'L3')}</td>
    <td class="c"><strong>${totalGeneral}</strong></td>
    <td class="c"><strong>${totalFillesGeneral}</strong></td>
  </tr>`;

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Bottin des étudiants finalistes — ${anneeAff}</title>
<style>
  @page { size: A4 landscape; margin: 10mm 12mm; }
  @media print { .no-print { display: none !important; } .page-break { page-break-before: always; } }
  body { font-family: 'Calibri', Arial, sans-serif; font-size: 10.5px; color: #000; margin: 0; padding: 0; line-height: 1.35; }

  .header { display: flex; align-items: center; gap: 18px; margin-bottom: 10px; }
  .header img { height: 60px; }
  .header .title { flex: 1; text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 0.5px; }

  .infos { margin: 10px 0; font-size: 12px; }
  .infos p { margin: 3px 0; }
  .infos .label { font-weight: 600; }
  .infos .cycle { margin-left: 16px; font-weight: 600; }

  .section-title { font-size: 12px; font-weight: bold; margin: 12px 0 5px; }
  .section-title .num { display: inline-block; margin-right: 4px; }

  .infos-gen { margin-left: 18px; margin-bottom: 8px; font-size: 11.5px; }
  .infos-gen .ligne { display: flex; align-items: baseline; gap: 4px; margin: 4px 0; }
  .infos-gen .ligne .lab { min-width: 220px; }
  .infos-gen .ligne .val { flex: 1; border-bottom: 1px dotted #666; padding-left: 6px; font-weight: 600; }

  table.grille { border-collapse: collapse; width: 100%; font-size: 9.5px; margin: 4px 0; table-layout: auto; }
  table.grille th, table.grille td { border: 1px solid #000; padding: 3px 4px; vertical-align: middle; }
  table.grille th { background: #f2f2f2; font-weight: bold; text-align: center; font-size: 9.5px; }
  table.grille td.c { text-align: center; }
  table.grille td.l { text-align: left; }
  table.grille td.moy { font-weight: 600; }
  table.grille tbody tr:nth-child(even) td { background: #fafafa; }
  table.grille th.moy-group { background: #e8e8e8; }

  table.recap { border-collapse: collapse; width: 100%; font-size: 11px; margin: 4px 0; }
  table.recap th, table.recap td { border: 1px solid #000; padding: 5px 8px; }
  table.recap th { background: #f2f2f2; font-weight: bold; text-align: center; }
  table.recap td.c { text-align: center; }
  table.recap td.l { text-align: left; }
  table.recap tr.total td { background: #efefef; font-weight: bold; }

  /* Page 2 */
  .obs-lines { margin: 8px 0 16px 18px; }
  .obs-lines .l { border-bottom: 1px dotted #666; height: 20px; margin: 8px 0; }
  .attestation { margin: 20px 18px; font-size: 12px; line-height: 1.9; text-align: justify; }
  .attestation .under { display: inline-block; border-bottom: 1px dotted #666; min-width: 260px; padding: 0 6px; }
  .fait-line { text-align: right; margin: 25px 18px 0; font-size: 12px; font-style: italic; }
</style></head><body>

<!-- ═══ PAGE 1 ═══ -->

<div class="header">
  <img src="/logo.png" alt="Logo 2i" onerror="this.style.display='none'" />
  <div class="title">BOTTIN DES ETUDIANTS FINALISTES</div>
  <div style="width: 60px;"></div>
</div>

<div class="infos">
  <p><span class="label">Année académique :</span> ${escapeHtml(anneeAff)}</p>
  <p><span class="label">Etablissement :</span> ${escapeHtml(etablissement)}</p>
  <p class="cycle">&#10148; Cycle : ${escapeHtml(cycleAff)}</p>
</div>

<div class="section-title"><span class="num">1.</span> Informations générales</div>
<div class="infos-gen">
  <div class="ligne">
    <span class="lab">&bull; Responsable académique :</span>
    <span class="val">${escapeHtml(responsable) || '&nbsp;'}</span>
  </div>
  <div class="ligne">
    <span class="lab">&bull; Effectif total des finalistes :</span>
    <span class="val"><strong>${data.effectif_total}</strong></span>
  </div>
</div>

<div class="section-title"><span class="num">2.</span> Liste des étudiants finalistes</div>
<table class="grille">
  <thead>
    <tr>
      <th rowspan="2" style="width: 26px;">N°</th>
      <th rowspan="2">Noms et Prénoms</th>
      <th rowspan="2" style="width: 30px;">Sexe</th>
      <th rowspan="2" style="width: 70px;">Date de naissance</th>
      <th rowspan="2" style="width: 70px;">Nationalité</th>
      <th rowspan="2" style="width: 70px;">Matricule</th>
      <th rowspan="2">Filière/Parcours</th>
      <th rowspan="2" style="width: 46px;">Année d'entrée</th>
      <th colspan="6" class="moy-group">Moyennes semestrielles</th>
      <th rowspan="2" style="width: 80px;">Téléphone</th>
      <th rowspan="2">Email</th>
    </tr>
    <tr>
      <th class="moy-group" style="width: 32px;">S1</th>
      <th class="moy-group" style="width: 32px;">S2</th>
      <th class="moy-group" style="width: 32px;">S3</th>
      <th class="moy-group" style="width: 32px;">S4</th>
      <th class="moy-group" style="width: 32px;">S5</th>
      <th class="moy-group" style="width: 32px;">S6</th>
    </tr>
  </thead>
  <tbody>
    ${rowsHtml || `<tr><td colspan="16" style="border:1px solid #000;padding:12px;text-align:center;font-style:italic;">Aucun étudiant finaliste pour ${escapeHtml(anneeAff)}</td></tr>`}
  </tbody>
</table>

<div class="section-title"><span class="num">3.</span> Récapitulatif par filière</div>
<table class="recap">
  <thead>
    <tr>
      <th>Filière/Parcours</th>
      <th>Niveau</th>
      <th>Effectif total des finalistes</th>
      <th>Dont filles</th>
    </tr>
  </thead>
  <tbody>
    ${recapRows || `<tr><td colspan="4" style="text-align:center;font-style:italic;">Aucun étudiant</td></tr>`}
  </tbody>
</table>

<!-- ═══ PAGE 2 ═══ -->
<div class="page-break"></div>

<div class="section-title" style="margin-top: 4px;"><span class="num">4.</span> Observations</div>
<div class="obs-lines">
  <div class="l"></div>
  <div class="l"></div>
  <div class="l"></div>
  <div class="l"></div>
</div>

<div class="section-title" style="margin-left: 18px;">&#10148; Attestation</div>
<div class="attestation">
  Je soussigné, <span class="under">${escapeHtml(responsable)}</span>,
  en qualité de <strong>Directeur des Affaires Académiques (DAAC)</strong>,
  atteste que les informations contenues dans le présent bottin sont exactes
  et conformes aux dossiers académiques de l'établissement.
</div>

<p class="fait-line">
  Fait à <strong>${escapeHtml(ville)}</strong>, le <strong>${fmtDate(dateEmission)}</strong>
</p>

</body></html>`;
}

// ── Utilitaire d'échappement HTML ──
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
